import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { useLanguage, pick } from "@/i18n";
import { adminFetch } from "@/utils/adminFetch";
import ImagePicker from "@/components/ImagePicker";

export default function AdminStoriesManager() {
  const { lang } = useLanguage();
  const [stories, setStories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [picker, setPicker] = useState(false);

  const load = () => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stories`)
      .then((r) => (r.ok ? r.json() : []))
      .then(setStories)
      .catch(() => {});
  };
  useEffect(load, []);

  const setLoc = (field, lng, value) => setEditing((s) => ({ ...s, [field]: { ...s[field], [lng]: value } }));

  const save = async () => {
    try {
      await adminFetch(`/admin/stories/${editing.slug}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: editing.title,
          excerpt: editing.excerpt,
          body: editing.body,
          minutes: Number(editing.minutes) || undefined,
          image: editing.image,
          destination: editing.destination,
        }),
      });
      toast.success("Storia aggiornata");
      setStories((ss) => ss.map((s) => (s.slug === editing.slug ? editing : s)));
      setEditing(null);
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="mt-10" data-testid="admin-stories">
      <div className="space-y-3">
        {stories.map((s) => (
          <div key={s.slug} className="border border-stone2/60 p-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm" data-testid={`admin-story-${s.slug}`}>
            <img src={s.image} alt="" className="w-16 h-12 object-cover border border-stone2/40" />
            <span className="font-medium text-ink min-w-[220px]">{pick(s.title, lang)}</span>
            <span className="text-ink/50 text-xs">{s.destination} · {s.minutes} min</span>
            <button
              onClick={() => setEditing({ ...s, title: { ...s.title }, excerpt: { ...s.excerpt }, body: { ...s.body } })}
              data-testid={`admin-story-edit-${s.slug}`}
              className="ml-auto bg-ink text-ivory text-[10px] font-medium uppercase tracking-[0.15em] px-4 py-2 hover:bg-medblue transition-colors duration-300"
            >
              Modifica
            </button>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[60] bg-ink/60 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 md:p-10" onClick={() => setEditing(null)} data-testid="admin-story-editor">
          <div className="bg-ivory max-w-3xl w-full border border-stone2/60 my-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-stone2/50">
              <p className="font-serif text-xl text-ink">{editing.title.it}</p>
              <button type="button" onClick={() => setEditing(null)} aria-label="Chiudi" data-testid="admin-story-close" className="text-ink/50 hover:text-ink transition-colors">
                <X size={18} strokeWidth={1.5} />
              </button>
            </div>
            <div className="p-6 space-y-5">
              {[
                ["Titolo", "title", false],
                ["Estratto", "excerpt", true],
                ["Testo della storia", "body", true],
              ].map(([label, field, area]) => (
                <div key={field} className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {["it", "en"].map((lng) => (
                    <div key={lng}>
                      <label className="text-[10px] uppercase tracking-[0.2em] text-ink/40">{label} · {lng.toUpperCase()}</label>
                      {area ? (
                        <textarea
                          value={editing[field][lng] || ""}
                          onChange={(e) => setLoc(field, lng, e.target.value)}
                          rows={field === "body" ? 8 : 3}
                          data-testid={`admin-story-${field}-${lng}`}
                          className="mt-1.5 w-full border border-stone2 bg-ivory px-4 py-3 text-sm text-ink focus:outline-none focus:border-medblue"
                        />
                      ) : (
                        <input
                          value={editing[field][lng] || ""}
                          onChange={(e) => setLoc(field, lng, e.target.value)}
                          data-testid={`admin-story-${field}-${lng}`}
                          className="mt-1.5 w-full border border-stone2 bg-ivory px-4 py-3 text-sm text-ink focus:outline-none focus:border-medblue"
                        />
                      )}
                    </div>
                  ))}
                </div>
              ))}
              <div className="grid grid-cols-2 gap-3 items-end">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-ink/40">Minuti di lettura</label>
                  <input
                    type="number"
                    value={editing.minutes}
                    onChange={(e) => setEditing((s) => ({ ...s, minutes: e.target.value }))}
                    data-testid="admin-story-minutes"
                    className="mt-1.5 w-full border border-stone2 bg-ivory px-4 py-3 text-sm text-ink focus:outline-none focus:border-medblue"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-ink/40">Foto di copertina</label>
                  <div className="mt-1.5 flex items-center gap-3">
                    <img src={editing.image} alt="" className="w-20 h-14 object-cover border border-stone2/50" />
                    <button
                      type="button"
                      onClick={() => setPicker(true)}
                      data-testid="admin-story-image-change"
                      className="border border-ink text-ink text-[10px] font-medium uppercase tracking-[0.15em] px-4 py-2.5 hover:bg-ink hover:text-ivory transition-colors duration-300"
                    >
                      Cambia foto
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-stone2/50">
              <button type="button" onClick={() => setEditing(null)} className="text-xs uppercase tracking-[0.15em] text-ink/50 hover:text-ink px-4 py-3 transition-colors duration-300">
                Annulla
              </button>
              <button
                type="button"
                onClick={save}
                data-testid="admin-story-save"
                className="bg-medblue text-ivory text-xs font-medium uppercase tracking-[0.2em] px-8 py-3.5 hover:bg-ink transition-colors duration-300"
              >
                Salva modifiche
              </button>
            </div>
          </div>
          {picker && (
            <ImagePicker
              onClose={() => setPicker(false)}
              onSelect={(src) => {
                setEditing((s) => ({ ...s, image: src }));
                setPicker(false);
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}
